import type { QueryKey, UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { useLayoutEffect, useRef, useState } from "react";
import { v4 } from "uuid";

import { queryClient } from "./utils";

type DataArray = unknown[];
type Queries<Data extends DataArray> = {
  [K in keyof Data]: UseQueryResult<Data[K]> | null | undefined;
};

export function getMergedStatus<Data extends DataArray>(
  queries: Queries<Data>
): UseQueryResult["status"] {
  return queries.some((query) => query?.isError)
    ? "error"
    : queries.every((query) => query?.isSuccess)
      ? "success"
      : "pending";
}

export function useMergeQuery<Data extends DataArray, OutputType>(
  queries: Queries<Data>,
  // only make transform optional if InputType can be an OutputType
  transform: (data: Data) => OutputType,
  customCacheKey?: QueryKey
) {
  const [cacheKeyPrefix] = useState(customCacheKey ?? v4());

  const status = getMergedStatus(queries.filter(Boolean));

  // when the data changes, we need to invalidate the result query to re-calculate its result data
  const hashKey = queries
    .filter(Boolean)
    .map((q) => q.dataUpdatedAt)
    .join(",");
  useLayoutEffect(() => {
    queryClient.invalidateQueries({ queryKey: [cacheKeyPrefix] });
  }, [cacheKeyPrefix, hashKey]);

  // useQuery fn is a closure; we need a ref to access the updated data in the closure
  const queriesRef = useRef(queries);
  queriesRef.current = queries;

  const transformRef = useRef(transform);
  transformRef.current = transform;

  return useQuery<OutputType, Error>({
    // without status, loading state will be awaited indefinitely
    // don't use hashKey here, as it causes full re-renders (overriding state); instead, invalidate in useEffect above
    queryKey: [cacheKeyPrefix, status],
    queryFn: () => {
      if (status === "error") {
        throw new Error();
      }

      if (status === "pending") {
        return new Promise(() => {}); // never resolves
      }

      // if all queries have succeeded, merge the data and transform into a new query
      const data = queriesRef.current.map((q) => q?.data);
      return (
        transformRef.current ? transformRef.current(data as Data) : data
      ) as OutputType;
    },
  });
}
